import React from "react";
import { Button, Input, Progress, UncontrolledTooltip } from "reactstrap";
import Select from "react-select";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { Column } from "Components/Common/CommonTableIndexWise";
import { validations } from "validations/FormValidations";
import { CoaDetailRow, AcceptanceFieldItem } from "./ipInventory.types";
import ip from "../css/ipInventory.module.scss";
import { IP_PATHS } from "../../../../Routes/paths";
import { formatDate } from "../../../../common/service/CommonService";
import TruncatedText from "Components/Common/TruncatedText";

export const getIPCategoryLabel = (code: string): string => {
  if (!code) return "";

  const upperCode = code.toUpperCase();

  if (upperCode.startsWith("T")) {
    return `Test Product (${code})`;
  }

  if (upperCode.startsWith("R")) {
    return `Reference Product (${code})`;
  }

  return code;
};

export const YES_NO_OPTIONS = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

export const YES_NO_ACCEPTANCE_OPTIONS = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

export const REQUIRED_QUANTITY_OPTIONS = [
  { label: "Sufficient", value: "Sufficient" },
  { label: "Insufficient", value: "Insufficient" },
];

export const EXPIRY_RETEST_TYPE_OPTIONS = [
  { label: "Expiry Date", value: "expiry" },
  { label: "Retest Date", value: "retest" },
];

export const BATCH_OR_LOT_OPTIONS = [
  { label: "Batch No.", value: "batch" },
  { label: "Lot No.", value: "lot" },
];

export const YES_NO_NA_OPTIONS = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
  { label: "N/A", value: "na" },
];

export const IP_ATTACHMENT_OPTIONS = [
  { label: "COA", value: "coa" },
  { label: "Other", value: "other" },
];

export const SELECT_QUERY_FIELDS = [
  "regulatoryofstudy",
  "productname",
  "genericname",
  "strength",
  "dosageform",
  "labelclaim",
  "lightcondition",
  "batchorlot",
  "batchlotno",
  "manufacturerlabel",
  "manufacturername",
  "manufacturingdate",
  "typeofdate",
  "expirydate",
  "unit",
  "storagecondition",
  "coareceiptdate",
];

export const getCoaTopFields = (
  selectedStudyCode: string | null,
  dateRestrictions?: {
    coareceiptdate?: any;
  },
  isFieldDisabled?: (fieldName: any) => boolean
): FormFieldConfig[] => {
  const isDisabled = isFieldDisabled ? isFieldDisabled("coareceiptdate") : !selectedStudyCode;
  return [
    {
      name: "coareceiptdate",
      label: "COA Receipt Date",
      type: "flatpickr",
      required: !isDisabled,
      disabled: isDisabled,
      placeholder: "Select Date",
      optionsProps: isDisabled ? undefined : dateRestrictions?.coareceiptdate,
    },
  ];
};

export const getContainerColumns = (
  containersList: any[],
  handleContainerChange: (index: number, field: any, val: any) => void,
  handleDeleteContainer: (index: number) => void,
  isDisabled: boolean,
  containerTypeOptions: any[] = [],
  containerConditionOptions: any[] = []
): Column[] => [
    {
      key: "containernumber",
      label: "Container Number",
      width: "140px",
      textCenter: true,
      render: (_val: any, _row: any, index = 0) => {
        const item = containersList[index];
        const isExisting = !!item?.isExisting;
        return (
          <div className="d-flex align-items-center justify-content-center">
            <span
              className={`badge ${isExisting ? "bg-secondary-subtle text-secondary border border-secondary" : "bg-light text-dark border"} fs-11 px-2.5 py-1 fw-bold d-flex align-items-center justify-content-center`}
              style={{ height: "26px", minWidth: "36px" }}
              title={isExisting ? "Existing container from IP Record" : "New container"}
            >
              {item?.containernumber || String(index + 1).padStart(2, "0")}
            </span>
          </div>
        );
      },
    },
    {
      key: "initialquantity",
      label: "Container Quantity",
      width: "160px",
      textCenter: true,
      render: (val: any, _row: any, index = 0) => {
        const rowDisabled = isDisabled || !!containersList[index]?.isExisting;
        return (
          <div className="d-flex align-items-center justify-content-center">
            <Input
              type="number"
              bsSize="sm"
              className="text-center px-2 py-0"
              style={{ height: "26px", fontSize: "11px" }}
              value={val ?? ""}
              onChange={(e) => handleContainerChange(index, "initialquantity", e.target.value)}
              disabled={rowDisabled}
              placeholder="Enter quantity..."
            />
          </div>
        );
      },
    },
    {
      key: "containertype",
      label: "Container Type",
      width: "170px",
      textCenter: true,
      render: (val: any, _row: any, index = 0) => {
        const rowDisabled = isDisabled || !!containersList[index]?.isExisting;
        return (
          <div className="d-flex align-items-center justify-content-center w-100">
            <Select
              options={containerTypeOptions}
              value={containerTypeOptions.find((opt) => String(opt.value) === String(val)) || null}
              onChange={(selected: any) =>
                handleContainerChange(index, "containertype", selected ? selected.value : null)
              }
              isDisabled={rowDisabled}
              isSearchable={true}
              menuPortalTarget={document.body}
              className="common-select w-100"
              classNamePrefix="common-select"
              placeholder="Select type..."
              styles={{
                control: (base: any) => ({
                  ...base,
                  minHeight: "26px",
                  height: "26px",
                  fontSize: "11px",
                }),
                valueContainer: (base: any) => ({
                  ...base,
                  padding: "0 6px",
                  height: "24px",
                }),
                indicatorsContainer: (base: any) => ({
                  ...base,
                  height: "24px",
                }),
                dropdownIndicator: (base: any) => ({
                  ...base,
                  padding: "0 4px",
                }),
                option: (base: any, state: any) => ({
                  ...base,
                  fontSize: "11px",
                  padding: "3px 8px",
                  height: "24px",
                  minHeight: "24px",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: state.isSelected
                    ? "#1e6b91"
                    : state.isFocused
                      ? "#e6f0ff"
                      : "white",
                  color: state.isSelected ? "white" : "black",
                }),
                menu: (base: any) => ({
                  ...base,
                  marginTop: "2px",
                }),
                menuList: (base: any) => ({
                  ...base,
                  padding: 0,
                }),
              }}
            />
          </div>
        );
      },
    },
    {
      key: "containercondition",
      label: "Container Condition",
      width: "160px",
      textCenter: true,
      render: (val: any, _row: any, index = 0) => {
        const rowDisabled = isDisabled || !!containersList[index]?.isExisting;
        return (
          <div className="d-flex align-items-center justify-content-center w-100">
            <Select
              options={containerConditionOptions}
              value={containerConditionOptions.find((opt) => String(opt.value) === String(val)) || null}
              onChange={(selected: any) =>
                handleContainerChange(index, "containercondition", selected ? selected.value : "")
              }
              isDisabled={rowDisabled}
              isSearchable={true}
              menuPortalTarget={document.body}
              className="common-select w-100"
              classNamePrefix="common-select"
              styles={{
                control: (base: any) => ({
                  ...base,
                  minHeight: "26px",
                  height: "26px",
                  fontSize: "11px",
                }),
                valueContainer: (base: any) => ({
                  ...base,
                  padding: "0 6px",
                  height: "24px",
                }),
                indicatorsContainer: (base: any) => ({
                  ...base,
                  height: "24px",
                }),
                dropdownIndicator: (base: any) => ({
                  ...base,
                  padding: "0 4px",
                }),
                option: (base: any, state: any) => ({
                  ...base,
                  fontSize: "11px",
                  padding: "3px 8px",
                  height: "24px",
                  minHeight: "24px",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: state.isSelected
                    ? "#1e6b91"
                    : state.isFocused
                      ? "#e6f0ff"
                      : "white",
                  color: state.isSelected ? "white" : "black",
                }),
                menu: (base: any) => ({
                  ...base,
                  marginTop: "2px",
                }),
                menuList: (base: any) => ({
                  ...base,
                  padding: 0,
                }),
              }}
            />
          </div>
        );
      },
    },
    {
      key: "isusable",
      label: "Not For Use",
      width: "100px",
      textCenter: true,
      render: (val: any, _row: any, index = 0) => {
        const rowDisabled = isDisabled || !!containersList[index]?.isExisting;
        const isNotInUse = val === false ;
        return (
          <div className="d-flex align-items-center justify-content-center">
            <Input
              type="checkbox"
              className="form-check-input my-0"
              style={{ cursor: rowDisabled ? "not-allowed" : "pointer" }}
              checked={isNotInUse}
              onChange={(e) => handleContainerChange(index, "isusable", !e.target.checked)}
              disabled={rowDisabled}
            />
          </div>
        );
      },
    },
    {
      key: "containerremarks",
      label: "Remarks",
      render: (val: any, _row: any, index = 0) => {
        const rowDisabled = isDisabled || !!containersList[index]?.isExisting;
        return (
          <div className="d-flex align-items-center">
            <Input
              type="text"
              bsSize="sm"
              className="px-2 py-0"
              style={{ height: "26px", fontSize: "11px" }}
              value={val ?? ""}
              onChange={(e) => handleContainerChange(index, "containerremarks", e.target.value)}
              disabled={rowDisabled}
              placeholder="Enter remarks..."
            />
          </div>
        );
      },
    },
    {
      key: "action",
      label: "Action",
      width: "70px",
      textCenter: true,
      render: (_val: any, _row: any, index = 0) => {
        const rowDisabled = isDisabled || !!containersList[index]?.isExisting;
        const newContainersCount = containersList.filter((c) => !c.isExisting).length;
        return (
          <div className="d-flex align-items-center justify-content-center">
            <Button
              type="button"
              color="danger"
              outline
              size="sm"
              className="btn-icon waves-effect waves-light border-0 d-flex align-items-center justify-content-center"
              style={{ width: "26px", height: "26px", padding: 0 }}
              onClick={() => handleDeleteContainer(index)}
              disabled={rowDisabled || newContainersCount <= 1}
              title="Delete container"
            >
              <i className="ri-delete-bin-line fs-13" />
            </Button>
          </div>
        );
      },
    },
  ];

export const getStudyProductTopFields = (
  studyOptions: any[],
  isFieldDisabled?: (fieldName: any) => boolean
): FormFieldConfig[] => [
  {
    name: "studycode",
    label: "Study Code",
    type: "select",
    required: true,
    options: studyOptions,
    disabled: true,
    placeholder: "Select study code...",
    rules: validations.ipInventory.studyid,
  },
  {
    name: "sponsorname",
    label: "Sponsor Name",
    type: "text",
    placeholder: "Auto-filled based on protocol",
    disabled: true,
  },
  {
    name: "regulatoryofstudy",
    label: "Regulatory of Study Submission",
    type: "text",
    required: true,
    disabled: isFieldDisabled ? isFieldDisabled("regulatoryofstudy") : false,
    placeholder: "Enter regulatory submission details...",
    rules: validations.ipInventory.regulatorysubmission,
  },
];

export const getStudyProductCategoryFields = (
  selectedStudyCode: string | null,
  editId: string | number | null,
  ipCategoryOptions: { label: string; value: string }[] = []
): FormFieldConfig[] => [
    {
      name: "ipcategory",
      label: "Type of Investigational Product",
      type: "radio",
      required: true,
      disabled: !selectedStudyCode || !!editId,
      options: ipCategoryOptions,
      rules: validations.ipInventory.ipcategory,
    },
  ];

export const getProductDetailsFields = (
  isFieldDisabled: (fieldName: any) => boolean,
  dosageformOptions: any[],
  lightConditionOptions: any[],
  manufacturerLabelOptions: any[],
  dateRestrictions?: {
    manufacturingdate?: any;
    expirydate?: any;
  },
  typeofdate: string = "expiry",
  batchorlot: string = "batch"
): FormFieldConfig[] => [
    {
      name: "productname",
      label: "Product Name (with dosage form and strength)",
      type: "text",
      required: true,
      disabled: isFieldDisabled("productname"),
      placeholder: "Enter product name as per label...",
      rules: validations.ipInventory.productname,
    },
    {
      name: "genericname",
      label: "Generic Name of the Product",
      type: "text",
      required: true,
      disabled: isFieldDisabled("genericname"),
      placeholder: "Enter generic name...",
      rules: validations.ipInventory.genericname,
    },
    {
      name: "strength",
      label: "Strength",
      type: "text",
      required: true,
      disabled: isFieldDisabled("strength"),
      placeholder: "e.g. 10mg",
      rules: validations.ipInventory.strength,
    },
    {
      name: "dosageform",
      label: "Dosage Form ",
      type: "select",
      required: true,
      disabled: isFieldDisabled("dosageform"),
      placeholder: "Select dosage form...",
      options: dosageformOptions,
      rules: validations.ipInventory.dosageform,
    },
    {
      name: "labelclaim",
      label: "Label Claim",
      type: "text",
      required: true,
      disabled: isFieldDisabled("labelclaim"),
      placeholder: "Enter label claim...",
      rules: validations.ipInventory.labelclaim,
    },
    {
      name: "lightcondition",
      label: "Light Condition",
      type: "select",
      required: true,
      options: lightConditionOptions,
      placeholder: "Select light condition...",
      disabled: isFieldDisabled("lightcondition"),
    },
    {
      name: "batchorlot",
      label: "Select Type",
      type: "radio",
      disabled: isFieldDisabled("batchorlot"),
      options: BATCH_OR_LOT_OPTIONS,
    },
    {
      name: "batchlotno",
      label: batchorlot === "lot" ? "Lot No." : "Batch No.",
      type: "text",
      required: true,
      disabled: isFieldDisabled("batchlotno"),
      placeholder: batchorlot === "lot" ? "Enter lot number..." : "Enter batch number...",
      rules: validations.ipInventory.batchlotno,
    },
    {
      name: "manufacturerlabel",
      label: "Manufacturer Label",
      type: "select",
      required: true,
      mutipleAllow: true,
      disabled: isFieldDisabled("manufacturerlabel"),
      options: manufacturerLabelOptions,
      placeholder: "Select label...",
    },
    {
      name: "manufacturername",
      label: "Manufacturer Name",
      type: "text",
      required: true,
      disabled: isFieldDisabled("manufacturername"),
      placeholder: "Enter manufacturer name...",
      rules: validations.ipInventory.manufacturername,
    },
    {
      name: "manufacturingdate",
      label: "Manufacturing Date",
      type: "flatpickr",
      required: true,
      disabled: isFieldDisabled("manufacturingdate"),
      optionsProps: dateRestrictions?.manufacturingdate,
    },
    {
      name: "typeofdate",
      label: "Select Date Type",
      type: "radio",
      disabled: isFieldDisabled("typeofdate"),
      options: EXPIRY_RETEST_TYPE_OPTIONS,
    },
    {
      name: "expirydate",
      label: typeofdate === "retest" ? "Retest Date" : "Expiry Date",
      type: "flatpickr",
      required: true,
      optionsProps: dateRestrictions?.expirydate,
      disabled: isFieldDisabled("expirydate"),
    },
  ];

export const getBatchDeliveryFields = (
  selectedStudyCode: string | null,
  unitOptions: any[],
  modeOfReceiptOptions: any[],
  packingConditionOptions: any[],
  dateRestrictions?: {
    dateofreceipt?: any;
  },
  isFieldDisabled?: (fieldName: any) => boolean,
  qtyforstudy?: string,
  qtyforretention?: string,
  selectedPackingCondition?: string | number
): FormFieldConfig[] => {
  const fields: FormFieldConfig[] = [
    {
      name: "receivedqty",
      label: "Received Quantity",
      type: "text",
      required: true,
      disabled: true,
      placeholder: "Auto-calculated from container quantities",
    },
    {
      name: "actualreceivedqty",
      label: "Actual Received Quantity",
      type: "text",
      required: true,
      disabled: true,
      placeholder: "Auto-calculated (usable containers)",
    },
    {
      name: "totalcontainers",
      label: "Total Containers",
      type: "number",
      required: true,
      disabled: true,
      placeholder: "Enter total containers",
      rules: validations.ipInventory.totalcontainers,
    },
    {
      name: "unit",
      label: "Unit",
      type: "select",
      required: true,
      disabled: isFieldDisabled ? isFieldDisabled("unit") : false,
      options: unitOptions,
      placeholder: "Select unit...",
      rules: validations.ipInventory.unit,
    },
    {
      name: "dateofreceipt",
      label: "Date of Receipt",
      type: "flatpickr",
      required: true,
      optionsProps: dateRestrictions?.dateofreceipt,
      disabled: isFieldDisabled ? isFieldDisabled("dateofreceipt") : !selectedStudyCode,
    },
    {
      name: "modeofreceipt",
      label: "Mode of Receipt",
      type: "select",
      required: true,
      disabled: !selectedStudyCode,
      options: modeOfReceiptOptions,
      placeholder: "Select mode of receipt...",
    },
    {
      name: "packingcondition",
      label: "Packing Condition",
      type: "select",
      required: true,
      disabled: !selectedStudyCode,
      options: packingConditionOptions,
      placeholder: "Select packing condition...",
    },
  ];

  const matchedPackingOpt = packingConditionOptions.find(
    (opt: any) => String(opt.value) === String(selectedPackingCondition)
  );
  const packingLabel = matchedPackingOpt ? matchedPackingOpt.label : String(selectedPackingCondition || "");
  if (String(packingLabel).trim().toLowerCase() === "unsealed") {
    fields.push({
      name: "packingconditionremarks",
      label: "Packing Condition Remarks",
      type: "text",
      required: true,
      disabled: !selectedStudyCode,
      placeholder: "Enter packing condition remarks...",
      rules: validations.ipInventory.packingconditionremarks,
    });
  }

  fields.push({
    name: "qtyforstudy",
    label: "Quantity required for study",
    type: "radio",
    required: true,
    options: REQUIRED_QUANTITY_OPTIONS,
    disabled: !selectedStudyCode,
  });

  if (qtyforstudy === "Insufficient") {
    fields.push({
      name: "qtyforstudyremarks",
      label: "Remarks for Study Quantity",
      type: "text",
      required: true,
      disabled: !selectedStudyCode,
      placeholder: "Enter remarks for study quantity...",
      rules: validations.ipInventory.qtyforstudyremarks,
    });
  }

  fields.push({
    name: "qtyforretention",
    label: "Quantity required for Retention (if applicable)",
    type: "radio",
    options: REQUIRED_QUANTITY_OPTIONS,
    disabled: !selectedStudyCode,
  });

  if (qtyforretention === "Insufficient") {
    fields.push({
      name: "qtyforretentionremarks",
      label: "Remarks for Retention Quantity",
      type: "text",
      required: true,
      disabled: !selectedStudyCode,
      placeholder: "Enter remarks for retention quantity...",
      rules: validations.ipInventory.qtyforretentionremarks,
    });
  }

  return fields;
};

export const getStorageConditionFields = (
  selectedStudyCode: string | null,
  isFieldDisabled?: (fieldName: any) => boolean
): FormFieldConfig[] => [
    {
      name: "storagecondition",
      label: "Required Storage Condition",
      type: "text",
      required: true,
      disabled: isFieldDisabled ? isFieldDisabled("storagecondition") : !selectedStudyCode,
      placeholder: "Enter storage condition...",
      rules: validations.ipInventory.storagecondition,
    },
  ];

export const getDataLoggerFields = (
  selectedStudyCode: string | null,
  showDataLoggerId: boolean = false,
  loggerprintobtained?: string | null,
  tempexcursionacceptable?: string | null
): FormFieldConfig[] => {
  const fields: FormFieldConfig[] = [
    {
      name: "isdatalogger",
      label: "Data Logger Accompanied",
      type: "radio",
      required: true,
      options: YES_NO_OPTIONS,
      disabled: !selectedStudyCode,
    },
  ];

  if (showDataLoggerId) {
    fields.push(
      {
        name: "dataloggerid",
        label: "Data Logger ID No.",
        type: "text",
        required: true,
        placeholder: "Enter Data Logger ID...",
        disabled: !selectedStudyCode,
        rules: validations.ipInventory.dataloggerid,
      },
      {
        name: "loggerprintobtained",
        label: "Printout of Data Logger Obtained",
        type: "radio",
        required: true,
        options: YES_NO_NA_OPTIONS,
        disabled: !selectedStudyCode,
      },
      {
        name: "tempexcursionacceptable",
        label: "Temp. Excursion Acceptable",
        type: "radio",
        required: true,
        options: YES_NO_NA_OPTIONS,
        disabled: !selectedStudyCode,
      }
    );

    const isPrintNoOrNa = loggerprintobtained === "no" || loggerprintobtained === "na";
    const isTempNoOrNa = tempexcursionacceptable === "no" || tempexcursionacceptable === "na";

    if (isPrintNoOrNa || isTempNoOrNa) {
      fields.push({
        name: "dataloggerremarks",
        label: "Data Logger Remarks",
        type: "text",
        required: true,
        disabled: !selectedStudyCode,
        placeholder: "Enter data logger remarks...",
        rules: validations.ipInventory.dataloggerremarks,
      });
    }
  } else {
    fields.push({
      name: "dataloggerremarks",
      label: "Data Logger Remarks",
      type: "text",
      required: true,
      disabled: !selectedStudyCode,
      placeholder: "Enter data logger remarks...",
      rules: validations.ipInventory.dataloggerremarks,
    });
  }

  return fields;
};

export const getPharmacyQuarantineFields = (
  selectedStudyCode: string | null,
  dateRestrictions?: {
    quarantinestartdate?: any;
    quarantineenddate?: any;
  },
  isFieldDisabled?: (fieldName: any) => boolean
): FormFieldConfig[] => [
    {
      name: "allottedpharmacyid",
      label: "Allotted Serially Pharmacy ID No.",
      type: "text",
      required: true,
      disabled: !selectedStudyCode,
      placeholder: "Enter Allotted Pharmacy ID...",
      rules: validations.ipInventory.allottedpharmacyid
    },
    {
      name: "quarantineinstrumentid",
      label: "Quarantine Instrument ID No.",
      type: "text",
      required: true,
      disabled: !selectedStudyCode,
      placeholder: "Enter Quarantine Instrument ID...",
      rules: validations.ipInventory.quarantineinstrumentid
    },
    {
      name: "quarantinestartdate",
      label: "Date of IMP Stored in Quarantine",
      type: "flatpickr",
      required: true,
      disabled: isFieldDisabled ? isFieldDisabled("quarantinestartdate") : !selectedStudyCode,
      optionsProps: dateRestrictions?.quarantinestartdate,
    },
    {
      name: "quarantineenddate",
      label: "Date of IMP Removed from Quarantine",
      type: "flatpickr",
      required: true,
      disabled: isFieldDisabled ? isFieldDisabled("quarantineenddate") : !selectedStudyCode,
      optionsProps: dateRestrictions?.quarantineenddate,
    },
  ];

export const getIpInventoryColumns = (
  handleViewClick: (row: any) => void
): any[] => [
    {
      key: "receiptnumber",
      label: "RECEIPT & STUDY",
      type: "link",
      onClick: (row: any) => handleViewClick(row),
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.link}>{row.receiptnumber || "-"}</p>
            <p className={ip.cellSecondary}>{row.studycode || "-"}</p>
          </>
        );
      },
    },
    {
      key: "productname",
      label: "PRODUCT",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>
              <TruncatedText text={row.productname || "-"} maxWidth="120px" />
            </p>
            <p className={ip.cellSecondary}>{getIPCategoryLabel(row.ipcategory) || "-"}</p>
          </>
        );
      },
    },
    {
      key: "batchno",
      label: "BATCH / LOT",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>
              {row.batchorlot === "lot" ? "Lot: " : "Batch: "}
              <TruncatedText text={row.batchlotno || "-"} maxWidth="85px" />
            </p>
            <p className={ip.cellSecondary}>
              Exp: {formatDate(row.expirydate) || "-"}
            </p>
          </>
        );
      },
    },
    {
      key: "receivedqty",
      label: "RECEIVED QUANTITY",
      textCenter: true,
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <span className={ip.quantityBadge}>
              {row.totalreceivedqty || "-"} {row.unittext || ""}
            </span>
            <p className={ip.cellSecondary}>
              Container: {row.totalcontainers} {row.containertypetext}
            </p>
          </>
        );
      },
    },
    {
      key: "storagecondition",
      label: "STORAGE",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}><TruncatedText text={row.storagecondition || "-"} maxWidth="90px" /></p>
          </>
        );
      },
    },
    {
      key: "dateofreceipt",
      label: "RECEIVED ON",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>
              {formatDate(row.dateofreceipt) || "-"}
            </p>
            <p className={ip.cellSecondary}>
              Sponsor: {row.sponsorname || "-"}
            </p>
          </>
        );
      },
    },
    {
      key: "verifiedby",
      label: "VERIFIED BY",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.verifiedbyname || "-"}</p>
            <p className={ip.cellSecondary}>
              {formatDate(row.verifieddate) || "-"}
            </p>
          </>
        );
      },
    },
    {
      key: "progress",
      label: "PROGRESS",
      textCenter: true,
      render: (_: any, row?: any) => {
        const targetId = `status-info-${String(row?.receiptnumber)}`;

        const steps = [
          {
            title: "IP Inventory Details",
            isCompleted: Boolean(row?.receiptnumber),
          },
          {
            title: "Acceptance Sheet",
            isCompleted: Boolean(row?.isacceptanceexist),
          },
          {
            title: "Verification",
            isCompleted: Boolean(row?.isverified),
          },
        ];

        const completedCount = steps.filter((s) => s.isCompleted).length;
        const totalCount = steps.length;
        const percentage = Math.round((completedCount / totalCount) * 100);

        let progressColor = "danger";
        if (percentage === 100) {
          progressColor = "success";
        } else if (percentage >= 66) {
          progressColor = "info";
        } else if (percentage >= 33) {
          progressColor = "warning";
        }

        return (
          <>
            <div
              id={targetId}
              className="d-flex flex-column align-items-center justify-content-center mx-auto"
              style={{ cursor: "pointer", width: "85px" }}
            >
              <div
                className="d-flex align-items-center justify-content-between w-100 mb-1"
                style={{ fontSize: "10px", lineHeight: 1 }}
              >
                <span className="fw-medium text-muted">{completedCount}/{totalCount}</span>
                <span className={`fw-bold text-${progressColor}`}>{percentage}%</span>
              </div>
              <Progress
                value={percentage}
                color={progressColor}
                style={{ height: "6px", width: "100%", borderRadius: "3px" }}
              />
            </div>
            <UncontrolledTooltip
              placement="left"
              target={targetId}
              innerClassName="mw-100 p-2"
              style={{ pointerEvents: "none", maxWidth: "250px" }}
            >
              <div className="d-flex flex-column text-start px-1 py-1" style={{ fontSize: "12px", pointerEvents: "none" }}>
                {steps.map((step, idx) => (
                  <React.Fragment key={idx}>
                    <div className="d-flex align-items-center gap-2">
                      <div className="d-flex align-items-center justify-content-center" style={{ width: "18px" }}>
                        {step.isCompleted ? (
                          <i className="ri-check-line text-white fw-bold fs-15" />
                        ) : (
                          <i className="ri-checkbox-blank-circle-line text-warning fs-13" />
                        )}
                      </div>
                      <span className={step.isCompleted ? "fw-medium text-white" : "text-white-50"} style={{ whiteSpace: "nowrap" }}>
                        {step.title}
                      </span>
                    </div>
                    {idx < steps.length - 1 && (
                      <div style={{ width: "18px", height: "14px" }} className="d-flex align-items-center justify-content-start">
                        <div
                          style={{
                            width: "2px",
                            height: "100%",
                            backgroundColor: step.isCompleted ? "#20c997" : "rgba(255, 255, 255, 0.4)",
                            marginLeft: "8px",
                            borderRadius: "1px",
                          }}
                        />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </UncontrolledTooltip>
          </>
        );
      },
    },
  ];

export const getAcceptanceFormFields = (
  containerNumberOptions: { label: string; value: string }[] = [],
  primarycontainerVal?: string,
  dateRestrictions?: {
    acceptancedate?: any;
  }
): FormFieldConfig[] => {
  const isPrimaryYes = primarycontainerVal === "yes";

  return [
    {
      name: "acceptancedate",
      label: "IP Acceptance Date",
      type: "flatpickr",
      required: true,
      optionsProps: {
        maxDate: "today",
        ...dateRestrictions?.acceptancedate,
      },
    },
    {
      name: "selectedcontainer",
      label: "Containers/pack selected for IP description",
      type: "select",
      options: containerNumberOptions,
      placeholder: "Select container number...",
      required: true,
    },
    {
      name: "primarycontainer",
      label: "Primary container (blister, strip and bottle) opened for IP description",
      type: "radio",
      options: YES_NO_ACCEPTANCE_OPTIONS,
      required: true,
    },
    {
      name: "trainingqty",
      label: "No. of IP unit removed for Training",
      type: "number",
      disabled: !isPrimaryYes,
      placeholder: "Enter quantity removed...",
      rules: validations.ipacceptance.trainingqty,
    },
    {
      name: "physicalverificationqty",
      label: "No. of IP unit removed for physical verification",
      type: "number",
      disabled: !isPrimaryYes,
      placeholder: "Enter quantity removed...",
      rules: validations.ipacceptance.physicalverificationqty,
    },
    {
      name: "assaydifference",
      label: "Assay value difference between Test versus Reference (%)",
      type: "text",
      disabled: !isPrimaryYes,
      placeholder: "Enter difference in %...",
      rules: validations.ipacceptance.assaydifference,
    },
    {
      name: "ipdescription",
      label: "Description of IP",
      type: "text",
      disabled: !isPrimaryYes,
      placeholder: "Enter description of IP...",
      rules: validations.ipacceptance.ipdescription,
    },
    {
      name: "comments",
      label: "Comments",
      type: "text",
      placeholder: "Enter comments...",
      rules: validations.ipacceptance.comments,
    },
  ];
};

export const getAcceptanceRecordFields = (data: any): AcceptanceFieldItem[] => [
  { label: "Study Code:", value: data?.studycode || "—" },
  { label: "Sponsor Name:", value: data?.sponsorname || "—" },
  { label: "Type of IP:", value: getIPCategoryLabel(data?.ipcategory) || "—" },
  { label: "Packing Condition:", value: data?.packingconditiontext || data?.packingcondition || "—" },
  { label: "Product Name:", value: data?.productname || "—" },
  { label: "Generic Name:", value: data?.genericname || "—" },
  { label: "Dosage Form:", value: data?.dosageformtext || data?.dosageform || "—" },
  { label: "Strength:", value: data?.strength || "—" },
  { label: "Label Claim:", value: data?.labelclaim || "—" },
  { label: "Light Condition:", value: data?.lightconditiontext || data?.lightcondition || "—" },
  { label: data?.batchorlot === "lot" ? "Lot No:" : "Batch No:", value: data?.batchlotno || data?.batchno || data?.lotno || "—" },
  { label: "Manufacturing Date:", value: data?.manufacturingdate ? formatDate(data.manufacturingdate) : "—" },
  { label: data?.typeofdate === "retest" ? "Retest Date:" : "Expiry Date:", value: data?.expirydate ? formatDate(data.expirydate) : "—" },
  { label: "Storage Condition:", value: data?.storagecondition || "—" },
  { label: data?.manufacturerlabeltext ? `${data.manufacturerlabeltext}:` : "Manufacturer Name:", value: data?.manufacturername || "—" },
];

